import React from 'react'
import {
    Grid,
    Card,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Chip,
    LinearProgress,
    useTheme,
    Container,
    IconButton,
    Badge,
} from '@mui/material'
import {
    AccessTime as Clock,
    CheckCircle,
    Warning as AlertTriangle,
    Build,
    ChatBubbleOutline,
} from '@mui/icons-material'
import { ServiceRequest } from '../../types/serviceRequest'
import { toMMDDYYYY } from '../../helpers/date'
import { getUiStatus } from '../../helpers'

interface Props {
    serviceRequestList: ServiceRequest[]
    onClickChannel: (serviceRequest: ServiceRequest) => void
}

export const ServiceRequests = (props: Props) => {
    const { serviceRequestList, onClickChannel } = props
    const theme = useTheme()

    const getPriorityColor = (priority: string | undefined) => {
        switch (priority) {
            case 'emergency':
                return theme.palette.error.main
            case 'high':
                return theme.palette.warning.main
            case 'medium':
                return theme.palette.info.main
            case 'low':
                return theme.palette.success.main
            default:
                return theme.palette.grey[500]
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return theme.palette.success.main
            case 'in_progress':
                return theme.palette.info.main
            case 'acknowledged':
                return theme.palette.warning.main
            case 'submitted':
                return theme.palette.grey[500]
            default:
                return theme.palette.grey[500]
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed':
                return <CheckCircle fontSize="small" />
            case 'in_progress':
                return <Build fontSize="small" />
            case 'acknowledged':
                return <Clock fontSize="small" />
            case 'submitted':
                return <AlertTriangle fontSize="small" />
            default:
                return <Clock fontSize="small" />
        }
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
    }

    const capitalizeCategory = (category: string) => {
        return category
            .split('_')
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(' ')
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                width: '100%',
            }}
        >
            <Grid container spacing={3} sx={{ maxWidth: '800px' }}>
                {serviceRequestList.length === 0 ? (
                    <Grid item xs={12}>
                        <Card
                            sx={{
                                bgcolor: 'rgba(138, 142, 140, 0.16)',
                                borderRadius: 2,
                                boxShadow: 'none',
                                border: '1px solid rgba(255,255,255,0.1)',
                                p: 3,
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'rgba(255,255,255,0.8)',
                                    mb: 1,
                                }}
                            >
                                No Service Requests
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'rgba(255,255,255,0.5)',
                                }}
                            >
                                You don't have any maintenance requests at the
                                moment.
                            </Typography>
                        </Card>
                    </Grid>
                ) : (
                    serviceRequestList.map((request: ServiceRequest) => (
                        <Grid item xs={12} key={request.id}>
                            <Card
                                sx={{
                                    bgcolor: 'rgba(138, 142, 140, 0.16)',
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                }}
                            >
                                <CardHeader
                                    title={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.8)',
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    {request.workorder?.title ||
                                                        'Unassigned Service Request'}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                    }}
                                                >
                                                    #{request.id}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    alignItems: 'flex-end',
                                                }}
                                            >
                                                {/* Priority - Reimplement later */}
                                                {/* <Chip
                                                    label={request.priority?.toUpperCase() || 'NO PRIORITY'}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: `${getPriorityColor(
                                                            request.priority,
                                                        )}15`,
                                                        color: getPriorityColor(
                                                            request.priority,
                                                        ),
                                                        fontWeight: 'medium',
                                                        border: '1px solid rgba(255,255,255,0.1)',
                                                    }}
                                                /> */}
                                                {request.workorder && (
                                                    <Chip
                                                        label={getUiStatus(request.workorder.status)}
                                                        style={{
                                                            backgroundColor:
                                                                theme.palette[
                                                                    request.workorder.status
                                                                ].main,
                                                            color: theme.palette.primary.contrastText,
                                                        }}
                                                        size="small"
                                                        sx={{ mb: 1 }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                                <CardContent>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.7)',
                                            mb: 2,
                                        }}
                                    >
                                        {request.description}
                                    </Typography>

                                    {/* Workorder Details & image */}
                                    <Grid container spacing={2}>
                                        {/* workorder details */}
                                        <Grid item xs={12} md={8}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                    }}
                                                >
                                                    Submitted:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.8)',
                                                    }}
                                                >
                                                    {toMMDDYYYY(new Date(request.created_date))}
                                                </Typography>
                                            </Box>
                                            {request.workorder?.start_date && (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent:
                                                            'space-between',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.5)',
                                                        }}
                                                    >
                                                        Service Date:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: 'rgba(255,255,255,0.8)',
                                                        }}
                                                    >
                                                        {toMMDDYYYY(
                                                            new Date(request.workorder.start_date),
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)',
                                                    }}
                                                >
                                                    Assigned to:
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.8)',
                                                    }}
                                                >
                                                    {request.workorder
                                                        ?.assigned_to?.name ||
                                                        'Not yet Assigned'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {/* image */}
                                        <Grid item xs={12} md={4}>
                                            <Container
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                            }}
                                            >
                                            {request.image && (
                                                <img
                                                    src={request.image}
                                                    alt="Service Request"
                                                    style={{
                                                        maxHeight: '90px',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            )}
                                            </Container>
                                        </Grid>
                                    </Grid>

                                    {/* Message Button */}
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                                        <IconButton
                                            onClick={(e) => {
                                                onClickChannel(request)
                                            }}
                                            sx={{ color: 'white' }}
                                        >
                                            <Badge
                                                badgeContent={request.message_count}
                                                color={
                                                    request.unread_count > 0
                                                        ? 'secondary'
                                                        : 'primary'
                                                }
                                            >
                                                <ChatBubbleOutline htmlColor='white'/>
                                            </Badge>
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                )}
            </Grid>
        </Box>
    )
}
